import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { GATES } from "@/data/temple";
import { slugify } from "@/lib/intro";
import { scrollToId, ease } from "@/lib/scroll";
import { useAuth } from "@/lib/auth";

const KEY = "tt-where-to-begin";
const FREE = GATES[4];

const STEPS = [
  { id: "map", title: "Read the map", text: "Thirteen gates, from the root to the waking life. Touch each one and let it speak. There is nothing to master yet.", cta: "See the 13 gates", go: { scroll: "gates" } },
  { id: "gate", title: `Walk Gate 05 · ${FREE.name}`, text: "The one gate open to every seeker. Read it slowly, then try its ritual in your own body.", cta: "Enter the free gate", go: { to: `/gates/${slugify(FREE.name)}` } },
  { id: "guide", title: "Receive the Starter Guide", text: "A free guide to your first practice, sent gently to your inbox.", cta: "Get the free guide", go: { scroll: "enter" } },
  { id: "initiate", title: "Become an initiate, when you’re ready", text: "Unlock every gate and every ritual with a single offering of $297 AUD.", cta: "Learn about initiation", go: { to: "/initiate" } },
];

const read = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "{}") ?? {};
  } catch {
    return {};
  }
};

const Step = ({ s, i, done, onGo }) => {
  const cls = "group mt-6 inline-flex items-center gap-2 font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#F2E8DF] border-b border-[#D9B47A]/50 pb-1 hover:text-[#D9B47A] hover:border-[#D9B47A] transition-colors";
  const inner = <>{s.cta}<ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" /></>;
  return (
    <motion.li
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay: i * 0.12, ease }}
      data-testid={`begin-step-${s.id}`}
      className="relative pt-8 border-t border-[#D9B47A]/20"
    >
      <div className="flex items-center gap-3">
        <span className={`w-8 h-8 rounded-full border flex items-center justify-center font-tt-mono text-[11px] transition-colors ${done ? "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]" : "border-[#D9B47A]/50 text-[#D9B47A]"}`}>
          {done ? <Check data-testid={`begin-step-${s.id}-done`} className="w-4 h-4" /> : String(i + 1).padStart(2, "0")}
        </span>
        {i === 1 && <span className="font-tt-mono text-[10px] uppercase tracking-[0.2em] text-[#E8838E] border border-[#C2414F]/60 rounded-full px-2 py-0.5">Free</span>}
      </div>
      <h3 className="font-display text-2xl sm:text-3xl mt-5 leading-snug">{s.title}</h3>
      <p className="mt-3 text-[15px] text-[#CFC2B6] leading-relaxed max-w-sm">{s.text}</p>
      {s.go.to ? (
        <Link to={s.go.to} data-testid={`begin-step-${s.id}-cta`} onClick={() => onGo(s.id)} className={cls}>{inner}</Link>
      ) : (
        <button data-testid={`begin-step-${s.id}-cta`} onClick={() => { onGo(s.id); scrollToId(s.go.scroll); }} className={cls}>{inner}</button>
      )}
    </motion.li>
  );
};

export default function WhereToBegin() {
  const { user } = useAuth();
  const [done, setDone] = useState(read);
  if (user?.initiate) return null;
  const onGo = (id) => {
    const next = { ...done, [id]: true };
    setDone(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // storage unavailable; progress simply isn't remembered
    }
  };
  const returning = Object.keys(done).length > 0;
  return (
    <section id="begin" data-testid="where-to-begin" className="relative bg-[#0A0706]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10 py-24 sm:py-32">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.9, ease }}>
          <div className="flex items-center gap-4 font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#D9B47A]">
            <span className="w-10 h-px bg-[#D9B47A]/60" />
            <span>{returning ? "Welcome back, seeker" : "New to the temple"}</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-8 max-w-3xl leading-[1.05]">
            Where to <em className="text-[#D9B47A]">begin.</em>
          </h2>
          <p className="mt-6 max-w-xl text-base sm:text-lg text-[#A8998C]">
            There is no rush here. Four gentle steps, taken in your own time, lead you from curiosity into the practice.
          </p>
        </motion.div>
        <ol className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-14">
          {STEPS.map((s, i) => <Step key={s.id} s={s} i={i} done={Boolean(done[s.id])} onGo={onGo} />)}
        </ol>
      </div>
    </section>
  );
}
